import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'

import { VolunteeringActionsMenu } from '../VolunteeringActionsMenu/VolunteeringActionsMenu'
import { CancelOpportunityModal } from '../CancelOpportunity/Modal/CancelOpportunityModal'
import { CancelOpportunityErrorModal } from '../CancelOpportunity/ErrorModal/CancelOpportunityErrorModal'
import { CancelOpportunitySuccessModal } from '../CancelOpportunity/SuccessModal/CancelOpportunitySuccessModal'
import { CloseOpportunityModal } from '../CloseOpportunity/Modal/CloseOpportunityModal'
import { CloseOpportunitySuccessModal } from '../CloseOpportunity/SuccessModal/CloseOpportunitySuccessModal'
import { CloseOpportunityErrorModal } from '../CloseOpportunity/ErrorModal/CloseOpportunityErrorModal'
import { PublishOpportunityModal } from '../PublishOpportunity/Modal/PublishOpportunityModal'
import { PublishOpportunitySuccessModal } from '../PublishOpportunity/SuccessModal/PublishOpportunitySuccessModal'
import { PublishOpportunityErrorModal } from '../PublishOpportunity/ErrorModal/PublishOpportunityErrorModal'
import { MoveToDraftOpportunityModal } from '../MoveToDraftOpportunity/Modal/MoveToDraftOpportunityModal'
import { MoveToDraftOpportunitySuccessModal } from '../MoveToDraftOpportunity/SuccessModal/MoveToDraftOpportunitySuccessModal'
import { MoveToDraftOpportunityErrorModal } from '../MoveToDraftOpportunity/ErrorModal/MoveToDraftOpportunityErrorModal'
import { useFeatureOpportunityActionModal } from '../hooks/UseFeatureOpportunityActionModal'
import { FeatureOpportunityModal } from '../FeatureOpportunityModal/FeatureOpportunityModal'
import { RemoveFeatureOpportunityModal } from '../FeatureOpportunityModal/RemoveFeatureOpportunityModal'
import { formatAttendeesCount } from '../formatAttendeesCount'

import {
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography,
  useToast
} from '@percent/lemonade'
import { env } from '@percent/partner-dashboard/env'
import { ErrorView, OpportunityStatusBadge } from '@percent/partner-dashboard/common/components'
import { POSTHOG_FEATURE_FLAGS, useMutation, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { OpportunitiesTableProps } from './OpportunitiesTable.types'
import styles from './OpportunitiesTable.module.scss'
import type {
  CancelOpportunityErrorCode,
  CloseOpportunityErrorCode,
  PublishOpportunityErrorCode,
  UnpublishOpportunityErrorCode
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import {
  OpportunityStatus,
  OpportunityType
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { FeaturedStarTooltip } from '@percent/partner-dashboard/common/components/FeaturedStarTooltip/FeaturedStarTooltip'
import { SearchAndFilters } from './SearchAndFilters/SearchAndFilters'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useCopyAttendeesEmails } from '@percent/partner-dashboard/common/hooks/useCopyAttendeesEmails/useCopyAttendeesEmails'
import { APIErrorHandler } from '@percent/partner-dashboard/common/library/APIErrorHandler'
import { downloadCsvFile } from '@percent/partner-dashboard/common/library/utility/utility'

export function OpportunitiesTable({
  data,
  isLoading,
  totalResults,
  errorMessage,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  refresh
}: Readonly<OpportunitiesTableProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    workplaceGivingService,
    reportingService: { getParticipantDataReport }
  } = useServices()
  const { userCan } = usePermissionCheck()
  const { addToast } = useToast()
  const isDraftsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_OPPORTUNITY_DRAFTS)

  const [selectedCloseOpportunity, setSelectedCloseOpportunity] = useState<{ id: string; name: string } | undefined>()
  const [closeErrorModalOpen, setCloseErrorModalOpen] = useState(false)
  const [closeSuccessModalOpen, setCloseSuccessModalOpen] = useState(false)
  const [closeErrorCode, setCloseErrorCode] = useState<CloseOpportunityErrorCode | undefined>(undefined)
  const [selectedCancelOpportunity, setSelectedCancelOpportunity] = useState<{ id: string; name: string } | undefined>()
  const [cancelErrorModalOpen, setCancelErrorModalOpen] = useState(false)
  const [cancelSuccessModalOpen, setCancelSuccessModalOpen] = useState(false)
  const [errorCode, setErrorCode] = useState<CancelOpportunityErrorCode | undefined>(undefined)
  const [selectedPublishOpportunity, setSelectedPublishOpportunity] = useState<
    { id: string; name: string } | undefined
  >()
  const [publishErrorModalOpen, setPublishErrorModalOpen] = useState(false)
  const [publishSuccessModalOpen, setPublishSuccessModalOpen] = useState(false)
  const [publishErrorCode, setPublishErrorCode] = useState<PublishOpportunityErrorCode | undefined>(undefined)
  const [selectedMoveToDraftOpportunity, setSelectedMoveToDraftOpportunity] = useState<
    { id: string; name: string } | undefined
  >()
  const [moveToDraftErrorModalOpen, setMoveToDraftErrorModalOpen] = useState(false)
  const [moveToDraftSuccessModalOpen, setMoveToDraftSuccessModalOpen] = useState(false)
  const [moveToDraftErrorCode, setMoveToDraftErrorCode] = useState<UnpublishOpportunityErrorCode | undefined>(undefined)
  const direction = (queryParams.direction as TableColumnSortDirection) ?? 'DESC'
  const sortBy = queryParams.sort ?? 'featuredAt'

  const {
    selectedFeaturedOpportunity,
    isFeaturedOpportunityModalOpen,
    openFeaturedOpportunityModal,
    closeFeaturedOpportunityModal,
    isRemoveFeaturedOpportunityModalOpen,
    openRemoveFeaturedOpportunityModal,
    closeRemoveFeaturedOpportunityModal
  } = useFeatureOpportunityActionModal()

  const [{ isLoading: isCancelLoading, error }, { apiFunc: cancelOpportunity }] = useMutation(
    workplaceGivingService.cancelVolunteeringOpportunity,
    res => {
      if (res.data?.error) {
        setCancelErrorModalOpen(true)
        setErrorCode(res.data?.error?.code)
      } else {
        setCancelSuccessModalOpen(true)
        refresh()
      }
    },
    err => {
      if (err) {
        setCancelErrorModalOpen(true)
      }
    }
  )

  const [{ isLoading: isCloseLoading }, { apiFunc: closeOpportunity }] = useMutation(
    workplaceGivingService.closeVolunteeringOpportunity,
    res => {
      if (res.data?.error) {
        setCloseErrorModalOpen(true)
        setCloseErrorCode(res.data?.error?.code)
      } else {
        setCloseSuccessModalOpen(true)
        refresh()
      }
    },
    err => {
      if (err) {
        setCloseErrorModalOpen(true)
      }
    }
  )

  const [{ isLoading: isPublishLoading, error: publishError }, { apiFunc: publishOpportunity }] = useMutation(
    workplaceGivingService.publishVolunteeringOpportunity,
    res => {
      if (res.data?.error) {
        setPublishErrorModalOpen(true)
        setPublishErrorCode(res.data?.error?.code)
      } else {
        setPublishSuccessModalOpen(true)
        refresh()
      }
    },
    err => {
      if (err) {
        setPublishErrorModalOpen(true)
      }
    }
  )

  const [{ isLoading: isMoveToDraftLoading, error: moveToDraftError }, { apiFunc: moveToDraftOpportunity }] =
    useMutation(
      workplaceGivingService.unpublishVolunteeringOpportunity,
      res => {
        if (res.data?.error) {
          setMoveToDraftErrorModalOpen(true)
          setMoveToDraftErrorCode(res.data?.error?.code)
        } else {
          setMoveToDraftSuccessModalOpen(true)
          refresh()
        }
      },
      err => {
        if (err) {
          setMoveToDraftErrorModalOpen(true)
        }
      }
    )

  const [csvDownloaderLoading, setCsvDownloaderLoading] = useState(false)
  const { copyEmails } = useCopyAttendeesEmails()

  const handleExportParticipantData = async (opportunityId: string) => {
    setCsvDownloaderLoading(true)
    try {
      const csvData = await getParticipantDataReport({ opportunityId })
      downloadCsvFile(csvData, `participant_data_${opportunityId}`)
      addToast(t('toast.volunteering.successMessage'), 'success')
    } catch (err: any) {
      addToast(APIErrorHandler(err?.response?.data?.error), 'error')
    } finally {
      setCsvDownloaderLoading(false)
    }
  }

  const handleRequestSort = (columnKey: string) => {
    const newDirection: TableColumnSortDirection = direction === 'DESC' ? 'ASC' : 'DESC'

    setQueryParams({
      direction: newDirection,
      sort: columnKey
    })
  }

  const handleCancelOpportunity = () => {
    if (selectedCancelOpportunity?.id) {
      setErrorCode(undefined)
      cancelOpportunity(selectedCancelOpportunity.id)
      setSelectedCancelOpportunity(undefined)
    }
  }

  const handleCloseOpportunity = () => {
    if (selectedCloseOpportunity?.id) {
      setCloseErrorCode(undefined)
      closeOpportunity(selectedCloseOpportunity.id)
      setSelectedCloseOpportunity(undefined)
    }
  }

  const handlePublishOpportunity = () => {
    if (selectedPublishOpportunity?.id) {
      setPublishErrorCode(undefined)
      publishOpportunity(selectedPublishOpportunity.id)
      setSelectedPublishOpportunity(undefined)
    }
  }

  const handleMoveToDraftOpportunity = () => {
    if (selectedMoveToDraftOpportunity?.id) {
      setMoveToDraftErrorCode(undefined)
      moveToDraftOpportunity(selectedMoveToDraftOpportunity.id)
      setSelectedMoveToDraftOpportunity(undefined)
    }
  }
  const columns = useMemo(
    () => [
      { id: '', content: '', props: { width: '2%' } },
      { id: 'name', content: t('table.header.name'), isSortable: true, props: { width: '21%' } },
      { id: t('table.header.nonprofit'), props: { width: '11%' } },
      { id: t('table.header.organizer'), props: { width: '20%' } },
      { id: 'attendees', content: t('table.header.attendees'), isSortable: true, props: { width: '12%' } },
      { id: 'startDate', content: t('table.header.startDate'), isSortable: true, props: { width: '13%' } },
      { id: t('table.header.status'), props: { width: '8%' } },
      { id: '', props: { width: '5%' } }
    ],
    [t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={
          queryParams?.query?.length || queryParams?.status?.length || queryParams?.visibility?.length
            ? t('typography.noSearchVolunteeringOpportunitiesTitle')
            : t('typography.noVolunteeringOpportunitiesTitle')
        }
        emptyTableDescription={
          queryParams?.query?.length || queryParams?.status?.length || queryParams?.visibility?.length
            ? t('typography.noSearchVolunteeringOpportunitiesDescription')
            : t('typography.noVolunteeringOpportunitiesDescription')
        }
        sortDirection={direction}
        sortByColumn={sortBy}
        handleRequestSort={handleRequestSort}
      >
        <SearchAndFilters queryParams={queryParams} setQueryParams={setQueryParams} />
        <Table>
          <TableBody>
            {data?.map(opportunity => {
              const opportunityHref = `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${opportunity.id}`
              const opportunityEditHref = `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${opportunity.id}/edit`
              const opportunityEditAttendanceHref = `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${opportunity.id}/edit-attendance`

              const sentenceCaseProvider = opportunity.external?.provider
                ? opportunity.external.provider.replace(/^./, char => char.toUpperCase())
                : undefined

              const OpportunityLink = (
                <a
                  className={styles.opportunitiesLink}
                  href={opportunityHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {opportunity.name.length > 38 ? `${opportunity.name.substring(0, 35)}...` : opportunity.name}
                </a>
              )

              return (
                <TableRow key={opportunity.id}>
                  {opportunity.featured && opportunity.status === OpportunityStatus.ACTIVE ? (
                    <TableCell>
                      <FeaturedStarTooltip featured={opportunity.featured} />
                    </TableCell>
                  ) : (
                    <TableCell>
                      <div />
                    </TableCell>
                  )}
                  <TableCell>
                    {opportunity.name.length > 38 ? (
                      <Tooltip content={opportunity.name}>{OpportunityLink}</Tooltip>
                    ) : (
                      OpportunityLink
                    )}
                  </TableCell>
                  <TableCell>{opportunity.organisation?.name || t('status.notAvailable')}</TableCell>
                  <TableCell>
                    {opportunity.organiser?.email ? (
                      <div className={styles.ownerWrapper}>
                        <Flex gap="0.5rem">
                          <Typography variant="default">{opportunity.organiser.fullName}</Typography>
                          {(opportunity.external?.provider || opportunity.managedByOrganisation) && (
                            <Tooltip
                              content={t('typography.volunteering.organizer.managedBy', {
                                organization: sentenceCaseProvider || opportunity.organisation?.name
                              })}
                            >
                              <span>
                                <Icon name="info" size={5} color="gray400" />
                              </span>
                            </Tooltip>
                          )}
                        </Flex>
                        <Typography variant="default">{opportunity.organiser.email}</Typography>
                      </div>
                    ) : (
                      <Typography variant="default">{t('status.notAvailable')}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {opportunity.type === OpportunityType.EVENT_SERIES ? (
                      <Link className={styles.sessionsLink} to={`/workplace-giving/volunteering/${opportunity.id}`}>
                        {t('volunteering.multipleSessions')}
                      </Link>
                    ) : (
                      formatAttendeesCount(opportunity, t)
                    )}
                  </TableCell>
                  <TableCell>
                    {opportunity.startDate ? getFormattedDate(opportunity.startDate) : t('table.cell.noDate')}
                  </TableCell>
                  <TableCell>
                    <OpportunityStatusBadge status={opportunity.status} />
                  </TableCell>
                  <TableCell>
                    <VolunteeringActionsMenu
                      items={[
                        {
                          key: 'view',
                          label: t('volunteering.viewOpportunity')
                        },
                        ...([OpportunityStatus.ACTIVE, OpportunityStatus.DRAFT].includes(opportunity.status) &&
                        userCan('manageOpportunities')
                          ? [
                              {
                                key: 'editOpportunity',
                                label: t('volunteering.editOpportunity')
                              }
                            ]
                          : []),
                        ...(isDraftsEnabled &&
                        opportunity.status === OpportunityStatus.DRAFT &&
                        userCan('manageOpportunities') &&
                        !opportunity.external
                          ? [
                              {
                                key: 'publishOpportunity',
                                label: t('volunteering.publishOpportunity')
                              }
                            ]
                          : []),
                        ...(isDraftsEnabled &&
                        opportunity.status === OpportunityStatus.ACTIVE &&
                        userCan('manageOpportunities') &&
                        !opportunity.external
                          ? [
                              {
                                key: 'moveToDraft',
                                label: t('volunteering.moveToDraft')
                              }
                            ]
                          : []),
                        ...([OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED].includes(opportunity.status) &&
                        userCan('manageOpportunities') &&
                        opportunity.type !== OpportunityType.EVENT_SERIES
                          ? [
                              {
                                key: 'editAttendance',
                                label: t('volunteering.editAttendance')
                              }
                            ]
                          : []),
                        ...(opportunity.status === OpportunityStatus.ACTIVE &&
                        opportunity.type !== OpportunityType.EVENT_SERIES &&
                        (!opportunity.startDate || new Date(opportunity.startDate) < new Date()) &&
                        userCan('manageOpportunities') &&
                        !opportunity.external
                          ? [
                              {
                                key: 'closeOpportunity',
                                label: t('volunteering.closeOpportunity')
                              }
                            ]
                          : []),
                        ...((opportunity.status === OpportunityStatus.CLOSED ||
                          (opportunity.status === OpportunityStatus.ACTIVE &&
                            (!opportunity.startDate || new Date(opportunity.startDate) > new Date()))) &&
                        userCan('manageOpportunities') &&
                        !opportunity.external
                          ? [
                              {
                                key: 'cancel',
                                label: t('volunteering.cancelOpportunity')
                              }
                            ]
                          : []),
                        ...(userCan('manageOpportunities') &&
                        opportunity.status === OpportunityStatus.ACTIVE &&
                        !opportunity.featured
                          ? [
                              {
                                key: 'featureOpportunity',
                                label: t('dialog.volunteering.feature.title'),
                                withSeparator: true
                              }
                            ]
                          : []),
                        ...(userCan('manageOpportunities') &&
                        opportunity.status === OpportunityStatus.ACTIVE &&
                        opportunity.featured?.featuredItemId
                          ? [
                              {
                                key: 'removeFeaturedOpportunity',
                                label: t('dialog.volunteering.remove'),
                                withSeparator: true
                              }
                            ]
                          : []),
                        ...(opportunity.type === OpportunityType.EVENT_SERIES
                          ? [
                              {
                                key: 'seeSessions',
                                label: t('button.seeSessions'),
                                withSeparator: true
                              }
                            ]
                          : []),
                        ...(opportunity.type !== OpportunityType.EVENT_SERIES &&
                        opportunity.status !== OpportunityStatus.DRAFT
                          ? [
                              {
                                key: 'copyEmails',
                                label: t('button.copyEmails'),
                                withSeparator: true,
                                dataTestId: 'copy-emails'
                              }
                            ]
                          : []),
                        // No client-side permission check — the backend controls CSV content
                        // based on user permissions (e.g. survey data only included if user has survey permissions).
                        // Series: GET participant-data returns opportunity_is_event_series (same as workplace-giving).
                        ...(opportunity.participants.count > 0 &&
                        opportunity.type !== OpportunityType.EVENT_SERIES &&
                        opportunity.status !== OpportunityStatus.DRAFT
                          ? [
                              {
                                key: 'exportParticipantData',
                                label: t('button.exportParticipantData'),
                                tooltipInfo: t('button.exportParticipantDataTooltip'),
                                dataTestId: 'export-participant-data'
                              }
                            ]
                          : [])
                      ]}
                      handleSelect={(actionKey: string) => {
                        switch (actionKey) {
                          case 'view':
                            window.open(opportunityHref, '_blank', 'noopener noreferrer')
                            break
                          case 'seeSessions':
                            navigate(`/workplace-giving/volunteering/${opportunity.id}`)
                            break
                          case 'editOpportunity':
                            window.open(opportunityEditHref, '_blank', 'noopener noreferrer')
                            break
                          case 'editAttendance':
                            window.open(opportunityEditAttendanceHref, '_blank', 'noopener noreferrer')
                            break
                          case 'closeOpportunity':
                            setSelectedCloseOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'cancel':
                            setSelectedCancelOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'publishOpportunity':
                            setSelectedPublishOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'moveToDraft':
                            setSelectedMoveToDraftOpportunity({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'featureOpportunity':
                            openFeaturedOpportunityModal({
                              id: opportunity.id,
                              name: opportunity.name
                            })
                            break
                          case 'removeFeaturedOpportunity':
                            openRemoveFeaturedOpportunityModal({
                              featuredItemId: opportunity.featured?.featuredItemId,
                              name: opportunity.name
                            })
                            break
                          case 'copyEmails':
                            copyEmails(opportunity.id)
                            break
                          case 'exportParticipantData':
                            if (!csvDownloaderLoading) {
                              handleExportParticipantData(opportunity.id)
                            }
                            break
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      {selectedCancelOpportunity ? (
        <CancelOpportunityModal
          open={!!selectedCancelOpportunity}
          opportunityName={selectedCancelOpportunity.name}
          handleCancelOpportunity={handleCancelOpportunity}
          handleClose={() => {
            setSelectedCancelOpportunity(undefined)
          }}
          isLoading={isCancelLoading}
        />
      ) : null}
      <CancelOpportunitySuccessModal
        open={cancelSuccessModalOpen}
        handleClose={() => {
          setCancelSuccessModalOpen(false)
        }}
      />
      {error || errorCode ? (
        <CancelOpportunityErrorModal
          open={cancelErrorModalOpen}
          handleClose={() => {
            setCancelErrorModalOpen(false)
          }}
          errorCode={(error?.code as CancelOpportunityErrorCode) || errorCode}
        />
      ) : null}
      {isFeaturedOpportunityModalOpen && selectedFeaturedOpportunity && (
        <FeatureOpportunityModal
          isOpen={isFeaturedOpportunityModalOpen}
          onClose={closeFeaturedOpportunityModal}
          opportunity={selectedFeaturedOpportunity}
          refresh={refresh}
        />
      )}
      {isRemoveFeaturedOpportunityModalOpen && selectedFeaturedOpportunity && (
        <RemoveFeatureOpportunityModal
          isOpen={isRemoveFeaturedOpportunityModalOpen}
          onClose={closeRemoveFeaturedOpportunityModal}
          opportunity={selectedFeaturedOpportunity}
          refresh={refresh}
        />
      )}
      {selectedCloseOpportunity ? (
        <CloseOpportunityModal
          open={!!selectedCloseOpportunity}
          opportunityName={selectedCloseOpportunity.name}
          handleCloseOpportunity={handleCloseOpportunity}
          handleClose={() => {
            setSelectedCloseOpportunity(undefined)
          }}
          isLoading={isCloseLoading}
        />
      ) : null}
      <CloseOpportunitySuccessModal
        open={closeSuccessModalOpen}
        handleClose={() => {
          setCloseSuccessModalOpen(false)
        }}
      />
      {error || closeErrorCode ? (
        <CloseOpportunityErrorModal
          open={closeErrorModalOpen}
          handleClose={() => {
            setCloseErrorModalOpen(false)
          }}
          errorCode={(error?.code as CloseOpportunityErrorCode) || closeErrorCode}
        />
      ) : null}
      {selectedPublishOpportunity ? (
        <PublishOpportunityModal
          open
          opportunityName={selectedPublishOpportunity.name}
          handlePublishOpportunity={handlePublishOpportunity}
          handleClose={() => {
            setSelectedPublishOpportunity(undefined)
          }}
          isLoading={isPublishLoading}
        />
      ) : null}
      <PublishOpportunitySuccessModal
        open={publishSuccessModalOpen}
        handleClose={() => {
          setPublishSuccessModalOpen(false)
        }}
      />
      {publishError || publishErrorCode ? (
        <PublishOpportunityErrorModal
          open={publishErrorModalOpen}
          handleClose={() => {
            setPublishErrorModalOpen(false)
          }}
          errorCode={(publishError?.code as PublishOpportunityErrorCode) || publishErrorCode}
        />
      ) : null}
      {selectedMoveToDraftOpportunity ? (
        <MoveToDraftOpportunityModal
          open={!!selectedMoveToDraftOpportunity}
          opportunityName={selectedMoveToDraftOpportunity.name}
          handleMoveToDraftOpportunity={handleMoveToDraftOpportunity}
          handleClose={() => {
            setSelectedMoveToDraftOpportunity(undefined)
          }}
          isLoading={isMoveToDraftLoading}
        />
      ) : null}
      <MoveToDraftOpportunitySuccessModal
        open={moveToDraftSuccessModalOpen}
        handleClose={() => {
          setMoveToDraftSuccessModalOpen(false)
        }}
      />
      {moveToDraftError || moveToDraftErrorCode ? (
        <MoveToDraftOpportunityErrorModal
          open={moveToDraftErrorModalOpen}
          handleClose={() => {
            setMoveToDraftErrorModalOpen(false)
          }}
          errorCode={(moveToDraftError?.code as UnpublishOpportunityErrorCode) || moveToDraftErrorCode}
        />
      ) : null}
    </>
  )
}
